import { FC, useMemo } from 'react';

import { useSelector } from 'react-redux';
import { DropdownFilter, useDropdown } from 'shared/components/DropdownFilter';
import { useExperiment, useRuntimeSetting, useShowSearchMetaTooltip } from 'shared/hooks';
import { useMortgageTypeFilter, useOnboarding } from './hooks';
import { FILTER_KEY } from './constants';
import { OnboardingContent } from './components';
import { selectMortgageMonthlyPaymentFilter } from 'shared/selectors/filters';
import { NEWBUILDING_MONTHLY_PAYMENT } from 'shared/constants/experiments';
import { DropdownMortgagePayment } from 'shared/components/DropdownFilter/components/DropdownMortgagePayment';
import { SingleSelectionAdvancedFilter } from 'shared/components/AdvancedFilter';
import { OnboardingTooltip } from '@cian/ui-kit';
import { MortgageMonthlyPaymentFilter } from 'shared/containers/MortgageMonthlyPaymentFilter';
import { selectIsInlineFiltersRedesigned } from 'shared/selectors/redesigned/selectIsInlineFiltersRedesigned';
import { selectPageType } from 'shared/selectors/page/selectPageType';
import { EPageType } from 'shared/types/page';

export const MortgagePaymentContainer: FC = () => {
  const { value } = useSelector(selectMortgageMonthlyPaymentFilter);
  const isFiltersRedesign = useSelector(selectIsInlineFiltersRedesigned);
  const pageType = useSelector(selectPageType);

  const mortgageTypeFilter = useMortgageTypeFilter();
  const onboarding = useOnboarding();
  const dropdownState = useDropdown(FILTER_KEY);
  const showTooltip = useShowSearchMetaTooltip(FILTER_KEY);

  const isNewBuildingMonthlyPaymentExperimentEnabled = useExperiment(NEWBUILDING_MONTHLY_PAYMENT);
  const isEnabled = useRuntimeSetting('Newbuilding.MortgageMonthlyPayment.Enabled', false);
  const isFiltersRedesignEnabled = useRuntimeSetting<boolean>('BX.FilterRedesign.Enabled', false);

  const label = useMemo(() => {
    if (!value) {
      return 'Ипотека';
    }

    if (value >= 1000000) {
      return `До ${Math.round(value / 1000000)} млн ₽/мес`;
    }

    if (value >= 1000) {
      return `До ${Math.round(value / 1000)} тыс ₽/мес`;
    }

    return `До ${value} ₽/мес`;
  }, [value]);

  if (
    !isNewBuildingMonthlyPaymentExperimentEnabled ||
    !isEnabled ||
    (isFiltersRedesign && pageType === EPageType.MAIN)
  ) {
    return null;
  }

  return (
    <OnboardingTooltip
      content={<OnboardingContent onClick={onboarding.onClose} />}
      theme="black"
      open={onboarding.isOpen}
      placement="bottom"
      onClose={onboarding.onClose}
    >
      <div>
        <DropdownFilter
          {...dropdownState}
          buttonLabel={label}
          showTooltip={showTooltip}
          isFiltersRedesignEnabled={isFiltersRedesignEnabled}
          dropdownContent={
            <DropdownMortgagePayment
              mortgageTypeFilter={
                <SingleSelectionAdvancedFilter
                  options={mortgageTypeFilter.options}
                  value={mortgageTypeFilter.value}
                  onFilterChange={mortgageTypeFilter.onFilterChange}
                />
              }
              mortgagePaymentFilter={<MortgageMonthlyPaymentFilter filterKey={FILTER_KEY} placeholder="Платёж до" />}
            />
          }
        />
      </div>
    </OnboardingTooltip>
  );
};
